<template>
  <ModuleWrapper
    class="tw-relative"
    :moduleId="moduleId"
    :image="imageSrcPath('assets/branding/button-card-color.svg')"
    title="Button & Card Color"
    @initializeData="initializeData"
  >
    <template #main>
      <ConditionalColorPicker
        id="button-card-bg-color-picker"
        label="Button Background Color"
        type="color"
        :initialColor="backgroundColor.style"
        @colorChange="onBackgroundColorChange"
      />

      <ConditionalColorPicker
        id="font-color-picker"
        label="Button Font Color"
        type="color"
        class="tw-mt-4"
        :initialColor="fontColor.style"
        @colorChange="onFontColorChange"
      />

      <div class="tw-mt-4 tw-flex tw-h-7 tw-items-center tw-justify-between">
        <div class="tw-text-xs">Screensaver color same as button color</div>
        <StudioToggle
          id="button-card-color-toggle"
          :checked="hideScreenSaverBackground"
          @update:checked="toggleScreenSaverBackground"
        />
      </div>

      <template v-if="!hideScreenSaverBackground">
        <ConditionalColorPicker
          id="screen-saver-bg-color-picker"
          label="Screen Saver Background Color"
          type="color"
          class="tw-mt-4"
          :initialColor="screenSaverBackgroundColor.style"
          @colorChange="onScreenSaverBgColorChange"
        />
        <ConditionalColorPicker
          id="screen-saver-font-color-picker"
          label="Screen Saver Font Color"
          type="color"
          class="tw-mt-4"
          :initialColor="screenSaverFontColor.style"
          @colorChange="onScreenSaverFontColorChange"
        />
      </template>
    </template>
  </ModuleWrapper>
</template>

<script>
import { mapMutations, mapGetters } from 'vuex';

import ModuleWrapper from './ModuleWrapper.vue';
import ConditionalColorPicker from '../components/ConditionalColorPicker.vue';
import { color } from '../data/defaultValues';

import StudioToggle from '@/studioComponents/StudioToggle.vue';
import { destructureRgbColor } from '@/utils/color.ts';

export default {
  name: 'ButtonAndCardColor',
  components: {
    ModuleWrapper,
    StudioToggle,
    ConditionalColorPicker
  },
  props: {
    moduleId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      backgroundColor: color,
      screenSaverBackgroundColor: color,
      fontColor: color,
      screenSaverFontColor: color,
      hideScreenSaverBackground: true
    };
  },
  created() {
    this.initializeData();
  },
  computed: {
    ...mapGetters('branding', {
      getInitialModuleData: 'getInitialModuleData'
    })
  },
  methods: {
    ...mapMutations('branding', {
      setChangesMadeInThemeObj: 'setChangesMadeInThemeObj'
    }),
    initializeData() {
      const moduleKeys = [
        'theme_secondary_color',
        'secondary_text_color',
        'screen_saver_background',
        'screen_saver_text_color'
      ];

      const moduleData = this.getInitialModuleData(moduleKeys);

      const { theme_secondary_color: themeSecondaryColor, secondary_text_color: secondaryTextColor } = moduleData;

      let { screen_saver_background: screenSaverBackground, screen_saver_text_color: screenSaverTextColor } =
        moduleData;

      if (screenSaverBackground === 'var(--theme-secondary-color)') {
        screenSaverBackground = themeSecondaryColor;
      }
      if (screenSaverTextColor === 'var(--secondary-text-color)') {
        screenSaverTextColor = secondaryTextColor;
      }

      if (themeSecondaryColor === screenSaverBackground && secondaryTextColor === screenSaverTextColor) {
        this.hideScreenSaverBackground = true;
      } else {
        this.hideScreenSaverBackground = false;
      }

      if (themeSecondaryColor) {
        this.backgroundColor = destructureRgbColor(themeSecondaryColor);
      }
      if (secondaryTextColor) {
        this.fontColor = destructureRgbColor(secondaryTextColor);
      }
      if (screenSaverBackground) {
        this.screenSaverBackgroundColor = destructureRgbColor(screenSaverBackground);
      }
      if (screenSaverTextColor) {
        this.screenSaverFontColor = destructureRgbColor(screenSaverTextColor);
      }
    },
    onBackgroundColorChange(color) {
      this.backgroundColor = color;
      this.setChangesMadeInThemeObj({
        theme_secondary_color: color.style
      });
    },
    onFontColorChange(color) {
      this.fontColor = color;
      this.setChangesMadeInThemeObj({
        secondary_text_color: color.style
      });
    },
    onScreenSaverBgColorChange(color) {
      this.screenSaverBackgroundColor = color;
      this.setChangesMadeInThemeObj({
        screen_saver_background: color.style
      });
    },
    onScreenSaverFontColorChange(color) {
      this.screenSaverFontColor = color;
      this.setChangesMadeInThemeObj({
        screen_saver_text_color: color.style
      });
    },
    toggleScreenSaverBackground() {
      this.hideScreenSaverBackground = !this.hideScreenSaverBackground;
      this.screenSaverBackgroundColor = this.backgroundColor;
      this.screenSaverFontColor = this.fontColor;

      if (this.hideScreenSaverBackground) {
        this.setChangesMadeInThemeObj({
          screen_saver_background: 'var(--theme-secondary-color)',
          screen_saver_text_color: 'var(--secondary-text-color)',
          onEditScreenSaver: false
        });
      } else {
        this.setChangesMadeInThemeObj({
          screen_saver_background: this.screenSaverBackgroundColor.style,
          screen_saver_text_color: this.screenSaverFontColor.style,
          onEditScreenSaver: true
        });
      }
    }
  }
};
</script>
